import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Star, Reply, Send, X } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../context/AuthContext";

const TEAL = "#0D9488";

/** Coach-side card showing every review received.
 *  For each review with no response yet, offer a Reply composer.
 */
export default function CoachReviewsCard() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyTarget, setReplyTarget] = useState(null); // {id}
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    api.get(`/coaches/${user.id}/ratings`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const submitReply = async () => {
    if (!replyTarget || !draft.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/coach/ratings/${replyTarget}/response`, { response: draft.trim() });
      const { data } = await api.get(`/coaches/${user.id}/ratings`);
      setInfo(data);
      setReplyTarget(null);
      setDraft("");
      toast.success("Reply posted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="bg-white border border-slate-200 rounded-xl p-6 h-40 animate-pulse" data-testid="coach-reviews-loading" />;
  }
  if (!info || info.rating_count === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="coach-reviews-card">
      <div className="flex items-center gap-2 mb-1">
        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
        <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-700">Your reviews</div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">
        {info.avg_rating?.toFixed?.(1) || info.avg_rating}
        <span className="text-slate-400 text-sm font-normal ml-2">from {info.rating_count} client{info.rating_count === 1 ? "" : "s"}</span>
      </h3>
      <p className="text-sm text-slate-500 mb-4">Public replies live directly under each review — keep them warm and brief.</p>

      <ul className="space-y-3" data-testid="coach-reviews-list">
        {info.recent.map((r) => (
          <li key={r.id} className="border border-slate-200 rounded-lg p-3" data-testid={`coach-review-row-${r.id}`}>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map((n) => (
                  <Star key={n} className={`w-3.5 h-3.5 ${n <= r.stars ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                ))}
              </div>
              <span className="text-[11px] text-slate-500 font-mono-num">@{r.reviewer_handle}</span>
              <span className="text-[10px] text-slate-400 ml-auto tabular-nums">{(r.created_at || "").slice(0, 10)}</span>
            </div>
            {r.comment && <div className="text-sm text-slate-700 mt-1.5 leading-relaxed">{r.comment}</div>}

            {r.response ? (
              <div className="mt-2 pl-3 border-l-2 border-teal-300 bg-teal-50/40 rounded-r py-2 pr-2">
                <div className="text-[9px] uppercase tracking-widest font-semibold text-teal-700 mb-0.5">Your reply</div>
                <div className="text-[13px] text-slate-800 leading-relaxed" data-testid={`coach-review-response-${r.id}`}>{r.response}</div>
              </div>
            ) : replyTarget === r.id ? (
              <div className="mt-2">
                <Textarea
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value.slice(0, 300))}
                  rows={2}
                  placeholder="Say thanks or address the feedback…"
                  className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
                  data-testid={`coach-review-reply-input-${r.id}`}
                />
                <div className="flex items-center justify-between mt-2">
                  <div className="text-[10px] text-slate-400 font-mono-num">{draft.length}/300</div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setReplyTarget(null); setDraft(""); }}
                            className="text-slate-500 hover:bg-slate-100"
                            data-testid={`coach-review-reply-cancel-${r.id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Cancel
                    </Button>
                    <Button type="button" size="sm" onClick={submitReply} disabled={!draft.trim() || busy}
                            className="text-white" style={{ background: TEAL }}
                            data-testid={`coach-review-reply-submit-${r.id}`}>
                      <Send className="w-3.5 h-3.5 mr-1" /> {busy ? "Posting…" : "Post reply"}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setReplyTarget(r.id); setDraft(""); }}
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-teal-700 hover:text-teal-900"
                data-testid={`coach-review-reply-open-${r.id}`}
              >
                <Reply className="w-3.5 h-3.5" /> Reply publicly
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
